import React, { useEffect } from "react";
import { safeLocalStorageSetItem } from "utils/localStorage";
import "./App.css";
import { Tooltip } from "radix-ui";
import {
  Redirect,
  Route,
  Switch,
  useLocation,
  useParams,
} from "react-router-dom";
import posthog from "posthog-js";
import FullScreenLoader, {
  FullScreenLoadingSpinner,
} from "components/FullScreenLoader";
import config from "config";
import AppContextProvider from "contexts/AppContext";
import OrgContextProvider from "contexts/OrgContext";
import CloudCredContextProvider from "contexts/CloudCredContext";
import InstanceTypesContextProvider from "contexts/InstanceTypesContext";
import PermissionsContextProvider from "contexts/PermissionsContext";
import SecretsContextProvider from "contexts/SecretsContext";
import UserContextProvider from "contexts/UserContext";
import WorkspaceContextProvider from "contexts/WorkspaceContext";
import EnvironmentList, {
  EnvironmentListRedirect,
} from "components/Environment/List";
import Logout from "components/Auth/Logout";
import { Toaster } from "sonner";
import Login from "components/Login";
import SecuredRoute from "SecuredRoute";
import Invite from "components/Invite";
import ErrorPage from "components/ErrorPages";
import LandingPage from "LandingPage";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import HelpCenter from "components/HelpCenter";
import { DashWrapper } from "components/DashboardContainer/UnauthenticatedDash";
import { useKas } from "contexts/KasContext";
import WorkspaceCreateContextProvider from "contexts/WorkspaceCreateContext";
import AccountSettings from "./components/AccountSettings";
import AppShellBoundary from "components/ApplicationShell/AppShellBoundary";
import EnvironmentSettings from "components/NodeDetail/Environment";
import { BillingProvider } from "contexts/BillingContext";
import OrgBillingPage, {
  OrgBillingPageRedirect,
} from "components/OrgPages/Billing";
import NotebookRenderer from "components/Notebooks/Notebooks";
import OneClickDeployContextProvider from "contexts/OneClickDeployContext";
import {
  EditLaunchableCreatorPage,
  LaunchableCreator,
} from "components/Launchables/Create";
import { LaunchablesRedirect } from "components/Launchables/Create/LaunchablesRedirect";
import LaunchableDeploy from "components/Launchables/DeployRedesign/Now";
import LaunchableDeployPublic from "components/Launchables/DeployRedesign/Public";
import NemoClawRedirect from "components/NemoClawRedirect";
import RedeemLaunchableCredits from "components/GiftCard/RedeemLaunchableCredits";
import LaunchableContextProvider from "contexts/LaunchableContext";
import NotebookRenderContextProvider from "contexts/NotebookRenderContext";
import EnvironmentCreateWrapper from "components/Environment/Create";
import RedeemableCode from "components/GiftCard/RedeemableCode";
import NodeDetail from "components/NodeDetail/BrevConnect";
import { BrevConnectRoute } from "components/BrevConnect/BrevConnectPage";
import { PUBLIC_ROUTES } from "SecuredRoute/isPublicRoute";
import EnvironmentCreatePublic from "components/Environment/Create/EnvironmentCreatePublic";
import { LOCALSTORAGE_ACTIVE_ORG_ID_KEY } from "constants/index";
import OrgSettings, { OrgSettingsRedirect } from "components/OrgSettings";
import AcceptInvite from "components/AcceptInvite";
import AcceptInvitationGate, {
  AcceptInvitationAcceptedPage,
  AcceptInvitationRejectedPage,
} from "components/OrgPages/AcceptInvitation";
import OrgPagePeople, { OrgTeamRedirect } from "components/OrgPages/Teams";
import AgentsPage from "components/Launchables/AgentsPage";
import LaunchablesPage from "components/Launchables";
import PhysicalAIPage from "components/Launchables/PhysicalAIPage";
import ManagedSecretsPage, {
  ManagedSecretsRedirect,
} from "components/ManagedSecrets";
import { ManagedSecretsFeatureGate } from "components/ManagedSecrets/ManagedSecretsFeatureGate";
import {
  isApplicationShellRedesigned,
  useApplicationShellPreference,
} from "components/ApplicationShell/ApplicationShellPreference";
import { buildOrganizationSettingsSecretHref } from "components/OrgSettings/settingsTabs";
import CliLogin from "components/CliLogin";
import {
  DEFAULT_POST_LOGIN_REDIRECT,
  getPostLoginRedirectOrDefault,
  setPostLoginRedirect,
  shouldPersistPostLoginRedirect,
} from "utils/redirects";

const Admin = React.lazy(() => import("components/Admin"));
const SecretDetailPage = React.lazy(
  () => import("components/ManagedSecrets/SecretDetail"),
);

const AdminRoute: React.FC = () => (
  <React.Suspense fallback={<FullScreenLoadingSpinner />}>
    <Admin />
  </React.Suspense>
);

const ManagedSecretsRoute: React.FC = () => (
  <ManagedSecretsFeatureGate>
    <ManagedSecretsPage />
  </ManagedSecretsFeatureGate>
);

const ManagedSecretDetailRoute: React.FC = () => {
  const shellPreference = useApplicationShellPreference();
  const location = useLocation();
  const { orgId, secretId } = useParams<{
    orgId: string;
    secretId: string;
  }>();

  return (
    <ManagedSecretsFeatureGate>
      {isApplicationShellRedesigned(shellPreference) ? (
        <React.Suspense fallback={<FullScreenLoadingSpinner />}>
          <SecretDetailPage />
        </React.Suspense>
      ) : (
        <Redirect
          to={buildOrganizationSettingsSecretHref(
            orgId,
            secretId,
            location.search,
          )}
        />
      )}
    </ManagedSecretsFeatureGate>
  );
};

const ManagedSecretsRedirectRoute: React.FC = () => (
  <ManagedSecretsFeatureGate>
    <ManagedSecretsRedirect />
  </ManagedSecretsFeatureGate>
);

const OrgScopedProviders: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <OrgContextProvider>
    <CloudCredContextProvider>
      <InstanceTypesContextProvider>{children}</InstanceTypesContextProvider>
    </CloudCredContextProvider>
  </OrgContextProvider>
);

const Callback: React.FC = () => {
  console.log("Route: /redirecting-you-swiftly");
  const redirectUrl = getPostLoginRedirectOrDefault();
  return <Redirect to={redirectUrl} />;
};

export const SHAKA = `
Your verb container is ready.
                               _
              _        ,-.    / )
             ( \`.     // /-._/ /
              \`\\ \\   /(_/ / / /
                ; \`-\`  (_/ / /
                |       (_/ /
                \\          /
                 )        /
    NVIDIA Brev /       /
`;
export const SHAKA2 = `
                               _
              _        ,-.    / )
             ( \`.     // /-._/ /
              \`\\ \\   /(_/ / / /
                ; \`-\`  (_/ / /
                |       (_/ /
                \\          /
  Let it rip,    )        /
  NVIDIA Brev   /       /
`;

if (window !== undefined) {
  (window as any).ph = posthog;
}

// Add these routes that need LaunchableContext
const LAUNCHABLE_ROUTES = [
  "/agents",
  "/launchables",
  "/launchables/create",
  "/physical-ai",
  "/org/:orgId/launchables",
  "/org/:orgId/launchables/metrics",
  "/org/:orgId/launchables/redeem",
  "/org/launchables",
];

// Load Stripe outside the component to ensure it's only called once
const stripePromise = loadStripe(config.stripePublishableKey);

const App: React.FC = () => {
  const location = useLocation();
  const { isUserLoggedIn, isLoading: isAuthLoading } = useKas();
  useEffect(() => {
    posthog.capture("$pageview");
  }, [location]);

  const appearance = {
    theme: "night",
    variables: {
      colorTextPlaceholder: "rgb(148 163 184)",
    },
    rules: {
      ".Input": {
        border: "1px solid rgb(30 41 59)",
        backgroundColor: "rgb(24 24 27)",
        color: "rgb(148 163 184)",
        boxShadow: "none",
      },
      ".Input:focus": {
        border: "1px solid rgb(6 182 212)",
        backgroundColor: "rgb(24 24 27)",
        color: "rgb(148 163 184)",
        boxShadow: "none",
      },
    },
  };
  const getActiveOrgIdFromQueryParam = () => {
    const queryParams = new URLSearchParams(window.location.search);
    return queryParams.get("activeOrgId");
  };

  React.useEffect(() => {
    document.title = "Console | Brev";
    // Preserve real app destinations in case another auth check sends the
    // browser away. Auth plumbing routes are filtered before storage.
    const pathname = window.location.pathname;
    if (shouldPersistPostLoginRedirect(pathname)) {
      setPostLoginRedirect(pathname + window.location.search);
    }

    const activeOrgId = getActiveOrgIdFromQueryParam();
    if (activeOrgId) {
      safeLocalStorageSetItem(LOCALSTORAGE_ACTIVE_ORG_ID_KEY, activeOrgId);
    }
  }, []);

  console.log("Route: /");

  return (
    <NotebookRenderContextProvider>
      <Switch>
        <Route exact path={PUBLIC_ROUTES.LANDING} component={LandingPage} />
        {/* /help, /agents, /launchables and /physical-ai are handled in two places:
            unauthed visitors get the public DashWrapper shell here; authed
            users fall through to the inner Switch below so the real Dash
            chrome (sidebar, org nav) renders. */}
        {!isAuthLoading && !isUserLoggedIn && (
          <Route
            exact
            path={PUBLIC_ROUTES.HELP}
            render={() => (
              <DashWrapper>
                <HelpCenter />
              </DashWrapper>
            )}
          />
        )}
        {!isAuthLoading && !isUserLoggedIn && (
          <Route
            exact
            path={PUBLIC_ROUTES.AGENTS}
            render={() => (
              <DashWrapper>
                <AgentsPage />
              </DashWrapper>
            )}
          />
        )}
        {!isAuthLoading && !isUserLoggedIn && (
          <Route
            exact
            path="/launchables"
            render={() => (
              <DashWrapper>
                <LaunchablesPage />
              </DashWrapper>
            )}
          />
        )}
        {!isAuthLoading && !isUserLoggedIn && (
          <Route
            exact
            path={PUBLIC_ROUTES.PHYSICAL_AI}
            render={() => (
              <DashWrapper>
                <PhysicalAIPage />
              </DashWrapper>
            )}
          />
        )}
        <Route path={PUBLIC_ROUTES.NOTEBOOK} component={NotebookRenderer} />
        <Route
          path={PUBLIC_ROUTES.CREATE_INSTANCE_PUBLIC}
          exact
          component={EnvironmentCreatePublic}
        />
        <Route
          path={PUBLIC_ROUTES.LAUNCHABLE_DEPLOY}
          exact
          component={LaunchableDeployPublic}
        />
        <Route
          path={PUBLIC_ROUTES.NEMOCLAW}
          exact
          component={NemoClawRedirect}
        />
        <Tooltip.Provider>
          <Toaster
            position="top-right"
            expand
            richColors
            closeButton
            duration={4000}
            gap={8}
          />
          <UserContextProvider>
            <OrgScopedProviders>
              <BillingProvider>
                <WorkspaceContextProvider>
                  <SecretsContextProvider>
                    <PermissionsContextProvider>
                      <AppContextProvider>
                        <Elements
                          stripe={stripePromise}
                          options={{ appearance } as any}
                        >
                          <Switch>
                            <Route
                              exact
                              path={PUBLIC_ROUTES.LOGIN}
                              component={Login}
                            />
                            <Route
                              exact
                              path={PUBLIC_ROUTES.REDEEM_CODE}
                              component={RedeemableCode}
                            />
                            <Route
                              exact
                              path="/accept-invite"
                              component={AcceptInvite}
                            />
                            <Route
                              exact
                              path={PUBLIC_ROUTES.ACCEPT_INVITATION_REJECTED}
                              component={AcceptInvitationRejectedPage}
                            />
                            <Route
                              exact
                              path={PUBLIC_ROUTES.ORG_ACCEPT_INVITATION}
                              component={AcceptInvitationGate}
                            />
                            <SecuredRoute
                              exact
                              path="/org/:orgId/accept-invitation/accepted"
                              component={AcceptInvitationAcceptedPage}
                            />
                            <SecuredRoute
                              exact
                              path="/invite"
                              component={Invite}
                            />
                            <WorkspaceCreateContextProvider>
                              <OneClickDeployContextProvider>
                                <AppShellBoundary>
                                  <FullScreenLoader>
                                    <Switch>
                                      <SecuredRoute
                                        exact
                                        path="/cli-login"
                                        component={CliLogin}
                                      />
                                      <SecuredRoute
                                        exact
                                        path="/redirecting-you-swiftly"
                                        component={Callback}
                                      />
                                      <SecuredRoute
                                        exact
                                        path="/org/:orgId/environments"
                                        component={EnvironmentList}
                                      />

                                      <SecuredRoute
                                        exact
                                        path="/org/environments"
                                        component={EnvironmentListRedirect}
                                      />
                                      <SecuredRoute
                                        exact
                                        path="/org/:orgId/team"
                                        component={OrgPagePeople}
                                      />
                                      <SecuredRoute
                                        exact
                                        path="/org/:orgId/secrets/:secretId"
                                        component={ManagedSecretDetailRoute}
                                      />
                                      <SecuredRoute
                                        exact
                                        path="/org/:orgId/secrets"
                                        component={ManagedSecretsRoute}
                                      />
                                      <SecuredRoute
                                        exact
                                        path="/org/secrets"
                                        component={ManagedSecretsRedirectRoute}
                                      />
                                      <SecuredRoute
                                        exact
                                        path="/org/team"
                                        component={OrgTeamRedirect}
                                      />
                                      <SecuredRoute
                                        exact
                                        path="/org/:orgId/settings"
                                        component={OrgSettings}
                                      />
                                      <SecuredRoute
                                        exact
                                        path="/org/settings"
                                        component={OrgSettingsRedirect}
                                      />
                                      <SecuredRoute
                                        exact
                                        path={[
                                          "/org/:orgId/brev-connect/new",
                                          "/org/:orgId/brev-connect/registered",
                                        ]}
                                        component={BrevConnectRoute}
                                      />
                                      <SecuredRoute
                                        exact
                                        path="/org/:orgId/environments/:deploymentId/:page"
                                        component={EnvironmentSettings}
                                      />
                                      <SecuredRoute
                                        exact
                                        path="/org/:orgId/nodes/:nodeId"
                                        component={NodeDetail}
                                      />
                                      <SecuredRoute
                                        exact
                                        path="/notebooks/:notebook"
                                        component={NotebookRenderer}
                                      />
                                      <SecuredRoute
                                        exact
                                        path="/org/:orgId/billing"
                                        component={OrgBillingPage}
                                      />
                                      <SecuredRoute
                                        exact
                                        path="/org/billing"
                                        component={OrgBillingPageRedirect}
                                      />
                                      <Route
                                        path={LAUNCHABLE_ROUTES}
                                        render={() => (
                                          <LaunchableContextProvider>
                                            <Switch>
                                              <Route
                                                exact
                                                path="/agents"
                                                component={AgentsPage}
                                              />
                                              <SecuredRoute
                                                exact
                                                path="/launchables/create"
                                                component={LaunchableCreator}
                                              />
                                              <SecuredRoute
                                                exact
                                                path="/launchables/:launchableId/edit"
                                                component={
                                                  EditLaunchableCreatorPage
                                                }
                                              />
                                              <Route
                                                exact
                                                path="/launchables"
                                                component={LaunchablesPage}
                                              />
                                              <Route
                                                exact
                                                path="/physical-ai"
                                                component={PhysicalAIPage}
                                              />
                                              <SecuredRoute
                                                exact
                                                path="/org/:orgId/launchables"
                                                component={LaunchablesPage}
                                              />
                                              <SecuredRoute
                                                exact
                                                path="/org/:orgId/launchables/metrics"
                                                render={({ match }) => (
                                                  <Redirect
                                                    to={`/org/${match.params.orgId}/launchables?tab=mine`}
                                                  />
                                                )}
                                              />
                                              <SecuredRoute
                                                exact
                                                path="/org/:orgId/launchables/redeem"
                                                component={
                                                  RedeemLaunchableCredits
                                                }
                                              />
                                              <SecuredRoute
                                                exact
                                                path="/org/launchables"
                                                component={LaunchablesRedirect}
                                              />
                                            </Switch>
                                          </LaunchableContextProvider>
                                        )}
                                      />
                                      <SecuredRoute
                                        exact
                                        path="/launchable/deploy/now"
                                        component={LaunchableDeploy}
                                      />
                                      <SecuredRoute
                                        exact
                                        path="/org/:orgId/environments/:workspaceId"
                                        component={EnvironmentSettings}
                                      />
                                      <SecuredRoute
                                        exact
                                        path="/environment/new"
                                        component={EnvironmentCreateWrapper}
                                      />
                                      <SecuredRoute
                                        exact
                                        path="/environments/new"
                                        component={EnvironmentCreateWrapper}
                                      />
                                      <Route
                                        exact
                                        path="/profile"
                                        render={() => (
                                          <Redirect to="/settings/account" />
                                        )}
                                      />
                                      <SecuredRoute
                                        path="/settings"
                                        component={AccountSettings}
                                      />
                                      <SecuredRoute
                                        path="/admin"
                                        component={AdminRoute}
                                      />
                                      <Route
                                        exact
                                        path={PUBLIC_ROUTES.LOGOUT}
                                        component={Logout}
                                      />
                                      <Route
                                        exact
                                        path={PUBLIC_ROUTES.ERROR}
                                        component={ErrorPage}
                                      />
                                      <Route
                                        exact
                                        path={PUBLIC_ROUTES.HELP}
                                        component={HelpCenter}
                                      />
                                      {!isAuthLoading && (
                                        <Redirect
                                          to={
                                            isUserLoggedIn
                                              ? DEFAULT_POST_LOGIN_REDIRECT
                                              : "/"
                                          }
                                        />
                                      )}
                                    </Switch>
                                  </FullScreenLoader>
                                </AppShellBoundary>
                              </OneClickDeployContextProvider>
                            </WorkspaceCreateContextProvider>
                          </Switch>
                        </Elements>
                      </AppContextProvider>
                    </PermissionsContextProvider>
                  </SecretsContextProvider>
                </WorkspaceContextProvider>
              </BillingProvider>
            </OrgScopedProviders>
          </UserContextProvider>
        </Tooltip.Provider>
      </Switch>
    </NotebookRenderContextProvider>
  );
};

export default App;
